// ==========================
// Version 1: pages/LandingPage2.js
// - Alternative landing page for side-by-side product positioning review
// - Leads with outcome and ownership before explaining the process
// ==========================
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import Galaxy from "../components/Galaxy";
import Seo from "../components/Seo";
import { useAuth } from "../context/AuthContext";
import "./LandingPage2.css";

const VALUE_ITEMS = [
  {
    number: "01",
    title: "Preserved as one story",
    text: "Your words, photographs, date and place stay connected as one complete memory.",
  },
  {
    number: "02",
    title: "Owned as a digital asset",
    text: "Your immortalised memory becomes a unique keepsake held through your crypto wallet.",
  },
  {
    number: "03",
    title: "Proven on the blockchain",
    text: "Its creation and ownership can be independently verified whenever it matters.",
  },
];

const STEPS = [
  {
    number: "1",
    title: "Tell the story",
    text: "Add the words, photographs and date that make the moment yours.",
  },
  {
    number: "2",
    title: "Connect the place",
    text: "Record where it happened and choose how precisely the location is shown.",
  },
  {
    number: "3",
    title: "Create its record",
    text: "Review exactly what you want to preserve as part of the lasting memory.",
  },
  {
    number: "4",
    title: "Make it yours forever",
    text: "Mint the memory as a unique digital asset connected to your wallet.",
  },
];

export default function LandingPage2({ enhancedMemoryCard = false, pagePath = "/landing-2" }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const menuButtonRef = React.useRef(null);
  const pageUrl =
    typeof window !== "undefined" ? `${window.location.origin}${pagePath}` : undefined;

  const beginMemory = () => {
    navigate(currentUser ? "/home" : "/register");
  };

  React.useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnOutside = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (menuButtonRef.current?.contains(event.target)) return;
      setMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);

  const navLinks = currentUser
    ? [
        { to: "/home", label: "Home" },
        { to: "/map", label: "Global Map" },
        { to: "/about", label: "About" },
      ]
    : [
        { to: "/map", label: "Global Map" },
        { to: "/about", label: "About" },
        { to: "/login", label: "Login" },
      ];

  return (
    <main className={`bwak-lp2${enhancedMemoryCard ? " bwak-lp2--enhanced" : ""}`}>
      <Seo
        title="Blessed with a Kiss | Immortalise your story"
        description="Immortalise the moments that made you who you are. Turn a meaningful memory into verifiable digital proof that is yours to keep."
        image="/images/first-kiss-memory.jpg"
        url={pageUrl}
        type="website"
      />

      <section className="bwak-lp2__hero" aria-labelledby="lp2-title">
        <div className="bwak-lp2__galaxy" aria-hidden="true">
          <Galaxy
            mouseRepulsion
            mouseInteraction
            density={1.3}
            glowIntensity={0.5}
            saturation={0.4}
            hueShift={240}
            twinkleIntensity={0.5}
            starSpeed={0.7}
            transparent
          />
        </div>

        <nav className="bwak-lp2__nav" aria-label="Landing page 2 navigation">
          <Link className="bwak-lp2__brand" to="/">
            <BrandLogo size={46} decorative framed />
            <span>Blessed With A Kiss</span>
          </Link>

          <div className="bwak-lp2__nav-links">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to}>{link.label}</Link>
            ))}
            <button type="button" onClick={beginMemory}>
              Immortalise a memory
            </button>
          </div>

          <button
            ref={menuButtonRef}
            className="bwak-lp2__menu-button"
            type="button"
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="bwak-lp2-mobile-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>

          {menuOpen ? (
            <div
              ref={menuRef}
              id="bwak-lp2-mobile-menu"
              className="bwak-lp2__mobile-menu"
            >
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMenuOpen(false)}>
                  {link.label}
                </Link>
              ))}
              <button type="button" onClick={beginMemory}>Immortalise a memory</button>
            </div>
          ) : null}
        </nav>

        <div className="bwak-lp2__preview-label">Some things deserve to last</div>
        <div className="bwak-lp2__hero-content">
          <h1 id="lp2-title">Immortalise the moments that made you who you are.</h1>
          <p className="bwak-lp2__hero-copy">
            Preserve a meaningful memory and turn it into a unique digital asset.
          </p>
          <div className="bwak-lp2__hero-actions">
            <button className="bwak-lp2__primary" type="button" onClick={beginMemory}>
              {currentUser ? "Immortalise a memory" : "Immortalise your first memory"}
            </button>
            <Link className="bwak-lp2__text-link" to="/about">
              Discover why BWK exists <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>

        <div className="bwak-lp2__hero-note">
          <span>Stories</span>
          <span>Places</span>
          <span>Proof</span>
          <span>Ownership</span>
        </div>
      </section>

      <section id="example" className="bwak-lp2__example-section">
        <div className="bwak-lp2__section-intro">
          <p className="bwak-lp2__eyebrow bwak-lp2__eyebrow--light">See what a memory becomes</p>
          <h2>More than something you save. Something you own.</h2>
          <p>
            A meaningful moment becomes a lasting digital keepsake. Its story,
            place and date stay together, connected to its owner.
          </p>
        </div>

        <article className="bwak-lp2__memory-card">
          <div className="bwak-lp2__memory-photo-wrap">
            <img
              src="/images/first-kiss-memory.jpg"
              alt="A fictional couple sharing their first kiss beside the River Thames at dusk"
            />
            <span>Example immortalised memory</span>
          </div>

          <div className="bwak-lp2__memory-body">
            <div className="bwak-lp2__memory-heading">
              <div>
                <p>Our first kiss</p>
                <h3>The Night We Knew</h3>
              </div>
              <div className="bwak-lp2__verified">
                <span aria-hidden="true">✓</span>
                Verified ownership
              </div>
            </div>

            <div className="bwak-lp2__memory-meta">
              <span>12 October 2024</span>
              <span>London, England</span>
            </div>

            <p className="bwak-lp2__memory-story">
              We stopped beside the river after walking for hours. The city carried
              on around us, but for one quiet moment, it felt as though the whole night
              belonged to us.
            </p>

            <dl className="bwak-lp2__proof-grid">
              <div><dt>Owner</dt><dd>0x7A3F...91C2</dd></div>
              <div><dt>Network</dt><dd>Ethereum</dd></div>
              <div><dt>Memory proof</dt><dd>#BWK-0001</dd></div>
            </dl>
          </div>
        </article>
      </section>

      <section className="bwak-lp2__steps-section">
        <div className="bwak-lp2__section-intro">
          <p className="bwak-lp2__eyebrow">How it works</p>
          <h2>From a meaningful moment to a memory that lasts.</h2>
        </div>

        <ol className="bwak-lp2__steps">
          {STEPS.map((step) => (
            <li key={step.number}>
              <span>{step.number}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            </li>
          ))}
        </ol>

        <button className="bwak-lp2__primary bwak-lp2__primary--dark" type="button" onClick={beginMemory}>
          {currentUser ? "Immortalise a memory" : "Immortalise your first memory"}
        </button>
      </section>

      <section className="bwak-lp2__value-section">
        <div className="bwak-lp2__section-intro bwak-lp2__section-intro--left">
          <p className="bwak-lp2__eyebrow bwak-lp2__eyebrow--light">What ownership gives you</p>
          <h2>One memory. A complete record. Yours to keep.</h2>
        </div>

        <div className="bwak-lp2__value-grid">
          {VALUE_ITEMS.map((item) => (
            <article key={item.number}>
              <span>{item.number}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>

        <div className="bwak-lp2__ownership-note">
          <BrandLogo size={44} decorative framed />
          <p>
            A photograph can be copied and an account can disappear. An immortalised
            memory carries its own record of origin and ownership.
          </p>
        </div>
      </section>

      <section className="bwak-lp2__future-section">
        <div className="bwak-lp2__future-mark" aria-hidden="true">∞</div>
        <div className="bwak-lp2__future-content">
          <p className="bwak-lp2__eyebrow">The value of what is real</p>
          <h2>
            The internet will contain unlimited generated content.
            <em> Proven human experiences will remain scarce.</em>
          </h2>
          <p>
            AI can create convincing images, voices, stories and identities. BWK gives
            you a way to establish that a story came from your life and belongs to you.
          </p>
        </div>
      </section>

      <section className="bwak-lp2__about-section">
        <div>
          <p className="bwak-lp2__eyebrow bwak-lp2__eyebrow--light">Why we built BWK</p>
          <h2>It began with one meaningful place and a question about how memories survive.</h2>
        </div>
        <Link to="/about">Read our story <span aria-hidden="true">→</span></Link>
      </section>

      <section className="bwak-lp2__final-section">
        <BrandLogo size={64} decorative framed />
        <p className="bwak-lp2__eyebrow">A memory mattered to you</p>
        <h2>Give it a place in the future.</h2>
        <p>
          Preserve the complete story, establish its origin and make it yours to keep.
        </p>
        <div className="bwak-lp2__final-actions">
          <button className="bwak-lp2__primary" type="button" onClick={beginMemory}>
            {currentUser ? "Immortalise a memory" : "Immortalise your first memory"}
          </button>
          <Link className="bwak-lp2__secondary-button" to="/about">
            Read the story behind BWK
          </Link>
        </div>
        {!currentUser ? (
          <Link className="bwak-lp2__account-link" to="/login">
            I already have an account
          </Link>
        ) : null}
      </section>
    </main>
  );
}
